import { useTranslation } from 'react-i18next';
import { Autocomplete, Grid, TextField } from '@mui/material';
import { useFormikContext } from 'formik';
import { isEmpty } from 'lodash/lang';
import { get } from 'lodash/object';

import useGeo from '../../../../../../../../../services/geo/useGeo';
import { NSC_ADDRESS } from '../../../../../../../../../services/i18n/namespaces';
import { normalizePostalCode } from '../../../../../../../../../services/normalizers';
import AddressValidator from '../AddressValidator';
import {
  useMileageAddressesActions,
  useMileageAddressesState
} from '../MileageAddressesContext/MileageAddressesContext';

import Address1Autocomplete from './Address1Autocomplete/Address1Autocomplete';
import CityAutocomplete from './CityAutocomplete/CityAutocomplete';

export default function AddressFields({ path }) {
  const { t } = useTranslation();
  const { mileageAddresses, syncing } = useMileageAddressesState(),
    { setAddressField, setCountryId, setCity } = useMileageAddressesActions();
  const { touched, errors, handleBlur } = useFormikContext();
  const data = get(mileageAddresses, path);

  const countryId = data.countryId;
  const regionId = data.regionId;
  const { countries, regions, matchedCountry, matchedRegion } = useGeo(countryId, regionId);

  return (
    <>
      <Grid size={4}>
        <Autocomplete
          value={matchedCountry}
          onChange={function({ target }, country) {
            setCountryId(path, country?.id || '');
          }}
          onBlur={function(e) {
            AddressValidator.revalidate(path);
            handleBlur(e);
          }}
          fullWidth
          getOptionKey={option => option.id}
          getOptionLabel={option => option.name ?? option}
          disablePortal
          options={countries}
          renderInput={params => (
            <TextField
              {...params}
              name={`mileageAddresses.${path}.countryId`}
              label={t(`${NSC_ADDRESS}:country`)}
              variant="standard"
              required
              error={getErrorBoolean('countryId')}
              helperText={getHelperText('countryId')}
              autoComplete="off"
            />
          )}
        />
      </Grid>
      <Grid size={4}>
        <Autocomplete
          disabled={isEmpty(regions)}
          value={matchedRegion}
          onChange={function({ target }, region) {
            setAddressField(`mileageAddresses.${path}.regionId`, region?.id || '');
          }}
          onBlur={function(e) {
            AddressValidator.revalidate(path);
            handleBlur(e);
          }}
          fullWidth
          getOptionKey={option => option.id}
          getOptionLabel={regionOptionLabel}
          disablePortal
          options={regions}
          renderInput={params => (
            <TextField
              {...params}
              name={`mileageAddresses.${path}.regionId`}
              label={data.countryId === 'CA' ? t(`${NSC_ADDRESS}:province`) : t(`${NSC_ADDRESS}:state`)}
              variant="standard"
              required
              error={getErrorBoolean('regionId')}
              helperText={
                getHelperText('regionId')
                  ? (data.countryId === 'CA' ? t(`${NSC_ADDRESS}:province`) : t(`${NSC_ADDRESS}:state`)) +
                    getHelperText('regionId')
                  : null
              }
              autoComplete="off"
            />
          )}
        />
      </Grid>
      <Grid size={4}>
        <CityAutocomplete
          name={`mileageAddresses.${path}.city`}
          matchedCountry={matchedCountry}
          matchedRegion={matchedRegion}
          city={data.city}
          onChange={function(value) {
            setCity(path, value);
          }}
          onBlur={function(e) {
            AddressValidator.revalidate(path);
            handleBlur(e);
          }}
          error={getErrorBoolean('city')}
          helperText={getHelperText('city')}
        />
      </Grid>
      <Grid size={6}>
        <Address1Autocomplete
          name={`mileageAddresses.${path}.address1`}
          matchedCountry={matchedCountry}
          matchedRegion={matchedRegion}
          city={data.city}
          address1={data.address1}
          valid={data.valid}
          onChange={function(value) {
            setAddressField(`mileageAddresses.${path}.address1`, value || '');
          }}
          onBlur={function(e) {
            AddressValidator.revalidate(path);
            handleBlur(e);
          }}
          error={getErrorBoolean('address1')}
          helperText={getHelperText('address1')}
        />
      </Grid>
      <Grid size={3}>
        <TextField
          name={`mileageAddresses.${path}.address2`}
          value={data.address2 || ''}
          onChange={function({ target }) {
            setAddressField(target.name, target.value || '');
          }}
          onBlur={handleBlur}
          error={getErrorBoolean('address2')}
          helperText={getHelperText('address2')}
          fullWidth
          label={t(`${NSC_ADDRESS}:address2`)}
          variant="standard"
          autoComplete="off"
        />
      </Grid>
      <Grid size={3}>
        <TextField
          name={`mileageAddresses.${path}.zipCode`}
          value={data.zipCode || ''}
          onChange={function({ target }) {
            setAddressField(target.name, normalizePostalCode(target.value, data.countryId));
          }}
          onBlur={function(e) {
            AddressValidator.revalidate(path);
            handleBlur(e);
          }}
          error={getErrorBoolean('zipCode')}
          helperText={getHelperText('zipCode')}
          fullWidth
          label={t(`${NSC_ADDRESS}:zip`)}
          variant="standard"
          required
          autoComplete="off"
        />
      </Grid>
    </>
  );
  function getErrorBoolean(fieldName) {
    const fieldTouched = get(touched, `mileageAddresses.${path}.${fieldName}`),
      fieldError = get(errors, `mileageAddresses.${path}.${fieldName}`);
    return !syncing && fieldTouched && Boolean(fieldError);
  }
  function getHelperText(fieldName) {
    const fieldTouched = get(touched, `mileageAddresses.${path}.${fieldName}`),
      fieldError = get(errors, `mileageAddresses.${path}.${fieldName}`);
    return !syncing && fieldTouched && fieldError;
  }
}

function regionOptionLabel(option) {
  let label = '';
  if (option.id) {
    label += option.id;
  }
  if (option.localName || option.name) {
    label += ` (${option.localName || option.name})`;
  }
  return label || option;
}
